import { DayStat } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  BGReadingFillIcon,
  ExerciseFillIcon,
  FoodFillIcon,
  InsulinFillIcon,
  BedtimeFillIcon,
  WakeUpFillIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import dayjs from "dayjs"
import type {
  ComponentPropsWithoutRef,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  useMemo,
} from "react"

import { useDayMetricContext } from "../DayMetricContext"
import { AverageGlucoseDayMetricComponent } from "./AverageGlucoseDayMetricComponent"
import { BedTimeGlucoseDayMetricComponent } from "./BedTimeGlucoseDayMetricComponent"
import styles from "./DayMetricComponent.module.scss"
import { ExerciseDayMetricComponent } from "./ExerciseDayMetricComponent"
import { NormalRangeDayMetricComponent } from "./NormalRangeDayMetricComponent"
import { StandardDeviationDayMetricComponent } from "./StandardDeviationDayMetricComponent"
import { TotalCaloriesDayMetricComponent } from "./TotalCaloriesDayMetricComponent"
import { TotalCarbsDayMetricComponent } from "./TotalCarbsDayMetricComponent"
import { TotalUnitsDayMetricComponent } from "./TotalUnitsDayMetricComponent"
import { WakeUpGlucoseDayMetricComponent } from "./WakeUpGlucoseDayMetricComponent"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useToday } from "src/hooks"

interface Props extends ComponentPropsWithoutRef<"button"> {
  dayStart: string;
}

/*
 * this component renders the metric that the user chooses to summarize
 * their day
 *
 * in order to do that it makes requests to the db
 */
export function DayMetricComponent(props: Props) {
  const polyglot = usePolyglot()
  const today = useToday()
  const { currentDeviceSettings } = useFollowedUsers()

  const {
    dayStart,
    ...buttonProps
  } = props

  const isToday = useMemo(
    () => dayjs(dayStart).isSame(
      today,
      "day",
    ),
    [
      today,
      dayStart,
    ],
  )

  const { openDayMetricModal } = useDayMetricContext()

  const dayMetric = currentDeviceSettings.day_stat

  const className = classNames(
    styles.container,
    props.className,
  )

  const propsOnClick = props.onClick

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      openDayMetricModal()
      if (propsOnClick) {
        propsOnClick(e)
      }
    },
    [
      propsOnClick,
      openDayMetricModal,
    ],
  )

  const childrenProps = {
    dayStart,
    ...buttonProps,
    className,
    onClick,
  }

  if (dayMetric === undefined && isToday) {
    return (
      <button
        {...buttonProps}
        className={className}
        onClick={onClick}
      >
        {polyglot.t("pages.home.activity_list.day_stat_cta")}
      </button>
    )
  }

  switch (dayMetric) {
    case DayStat.AverageGlucose:
      return (
        <AverageGlucoseDayMetricComponent
          {...childrenProps}
          id={"feed-header-average"}
        >
          <BGReadingFillIcon className={styles.icon} />
        </AverageGlucoseDayMetricComponent>
      )
    case DayStat.WakeUpGlucose:
      return (
        <WakeUpGlucoseDayMetricComponent
          {...childrenProps}
          id={"feed-header-average-day"}
        >
          <WakeUpFillIcon className={styles.icon} />
        </WakeUpGlucoseDayMetricComponent>
      )
    case DayStat.BedTimeGlucose:
      return (
        <BedTimeGlucoseDayMetricComponent
          {...childrenProps}
          id={"feed-header-average_night"}
        >
          <BedtimeFillIcon className={styles.icon} />
        </BedTimeGlucoseDayMetricComponent>
      )
    case DayStat.Exercise:
      return (
        <ExerciseDayMetricComponent
          {...childrenProps}
          id={"feed-header-exercise"}
        >
          <ExerciseFillIcon className={styles.icon} />
        </ExerciseDayMetricComponent>
      )
    case DayStat.NormalRange:
      return (
        <NormalRangeDayMetricComponent
          {...childrenProps}
          id={"feed-header-normal_range"}
        />
      )
    case DayStat.StandardDeviation:
      return (
        <StandardDeviationDayMetricComponent
          {...childrenProps}
          id={"feed-header-stddev"}
        />
      )
    case DayStat.TotalCalories:
      return (
        <TotalCaloriesDayMetricComponent
          {...childrenProps}
          id={"feed-header-calories"}
        />
      )
    case DayStat.TotalCarbs:
      return (
        <TotalCarbsDayMetricComponent
          {...childrenProps}
          id={"feed-header-carbs"}
        >
          <FoodFillIcon className={styles.icon} />
        </TotalCarbsDayMetricComponent>
      )
    case DayStat.TotalUnits:
      return (
        <TotalUnitsDayMetricComponent
          {...childrenProps}
          id={"feed-header-units"}
        >
          <InsulinFillIcon className={styles.icon} />
        </TotalUnitsDayMetricComponent>
      )
  }

  return null
}
